import { useEffect } from 'react';
import { Types } from '@cornerstonejs/core';

interface UseVideoAnimationLoopProps {
  viewport: unknown | null;
  isVideoViewport: boolean;
  /** Skip frame state updates while user is actively seeking */
  seekingRef: React.MutableRefObject<boolean>;
  setCurrentFrame: (frame: number) => void;
  setIsPlaying: (playing: boolean) => void;
}

/**
 * Drives a `requestAnimationFrame` loop that mirrors a Cornerstone IVideoViewport's
 * playback state into React. Cornerstone's `VideoViewport.play()` does not always
 * start its internal render loop, so we call `render()` ourselves each frame while
 * playing. State setters are called only on actual changes to avoid extra re-renders.
 */
export function useVideoAnimationLoop({
  viewport,
  isVideoViewport,
  seekingRef,
  setCurrentFrame,
  setIsPlaying,
}: UseVideoAnimationLoopProps): void {
  useEffect(() => {
    if (!isVideoViewport || !viewport) return () => {};

    const videoViewport = viewport as Types.IVideoViewport;
    let animationFrameId: number | null = null;
    let lastFrame = -1;
    let lastPlayingState = false;

    const videoElement =
      (videoViewport as unknown as { videoElement?: HTMLVideoElement })
        .videoElement ||
      (videoViewport as unknown as { video?: HTMLVideoElement }).video;

    const updateVideoState = () => {
      try {
        // Cornerstone's getFrameNumber() is 1-based; convert to 0-based for the app
        const frame = videoViewport.getFrameNumber() - 1;
        const playing = videoElement
          ? !videoElement.paused && !videoElement.ended
          : false;

        // Cornerstone's VideoViewport.play() may not start its internal render loop
        // so we call render() ourselves to draw video frames to the canvas
        if (playing) {
          videoViewport.render();
        }

        // Skip frame updates while user is seeking to prevent slider from jumping back
        if (frame !== lastFrame && !seekingRef.current) {
          lastFrame = frame;
          setCurrentFrame(frame);
        }

        if (playing !== lastPlayingState) {
          // Render one final frame when pausing to preserve the current frame on canvas
          // This prevents blank screen on browsers that clear unrendered WebGL canvases (e.g., macOS Chrome)
          if (!playing && lastPlayingState) {
            videoViewport.render();
          }
          lastPlayingState = playing;
          setIsPlaying(playing);
        }
      } catch {
        // Viewport may have been disposed mid-loop — stop the RAF loop gracefully.
        return;
      }

      animationFrameId = requestAnimationFrame(updateVideoState);
    };

    animationFrameId = requestAnimationFrame(updateVideoState);

    // Also listen to video element events for immediate state updates
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => {
      try {
        videoViewport.render();
      } catch {
        // Viewport might be disposed
      }
      setIsPlaying(false);
    };
    const handleEnded = () => setIsPlaying(false);

    if (videoElement) {
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);
    }

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      if (videoElement) {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
      }
    };
  }, [isVideoViewport, viewport, seekingRef, setCurrentFrame, setIsPlaying]);
}
